/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { useHeaderData } from '@/features/header/api/getHeader';
import { defaultStoreUS } from '@/constants/locale';
import { useOMSFlags } from '@/features/product/utils/getOMSFlags';
import type { AxiosInstance } from 'axios';
import type { Country } from '@/types';
import type { QueryClient } from '@tanstack/react-query';
import environmentConfig from '@/config/environment';

type InventoryFulfillmentOption = {
  available: boolean;
  fulfillmentId: number;
  availableQty: number;
};

export type CertonaInventoryResponse = {
  inventory: Record<string, InventoryFulfillmentOption[]>;
  inventorySource: 'CIS' | 'OMS';
  totalSkus: number;
  skusWithInventory: number;
};

type CertonaInventoryOptions = {
  skuIds: string[];
  country: Country;
  storeId: string;
  inventorySource?: 'CIS' | 'OMS';
};

const INVENTORY_API_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-browse-search-data/v1/inventories`;

const getCertonaInventoryOptions = async (
  options: CertonaInventoryOptions,
  axiosInstance?: AxiosInstance,
): Promise<CertonaInventoryResponse> => {
  const { skuIds, country, storeId, inventorySource } = options;

  const headers: Record<string, string> = {};

  if (inventorySource) {
    headers['inventory-source'] = inventorySource;
  }

  const response = await getAxios(axiosInstance).get<CertonaInventoryResponse>(INVENTORY_API_URL, {
    params: {
      skuIds: skuIds.join(','),
      country,
      storeId,
    },
    headers,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return response.data;
};

const {
  useData,
  prefetch: prefetchCertonaInventoryOptions,
  query: certonaInventoryQuery,
  getFromCache,
} = createQuery<CertonaInventoryResponse, CertonaInventoryOptions>(
  'certonaInventoryOptions',
  getCertonaInventoryOptions,
);

export const useCertonaInventoryOptions = (skuIds: string[]) => {
  const country = 'USA';
  const { data: headerData } = useHeaderData();
  const storeId = headerData?.storeNumber ?? defaultStoreUS;
  const { useOMSInventoryQohService } = useOMSFlags();
  const inventorySource = useOMSInventoryQohService ? 'OMS' : 'CIS';

  return useData({
    skuIds,
    country,
    storeId,
    inventorySource,
    enabled: skuIds.length > 0,
  });
};

export const prefetchCertonaInventory = (
  queryClient: QueryClient,
  skuIds: string[],
  country: Country,
  storeId: string,
  axiosInstance: AxiosInstance,
  inventorySource?: 'CIS' | 'OMS',
) => {
  return prefetchCertonaInventoryOptions(
    queryClient,
    { skuIds, country, storeId, inventorySource },
    axiosInstance,
  );
};

export const getCertonaInventoryFromCache = (
  queryClient: QueryClient,
  skuIds: string[],
  country: Country,
  storeId: string,
  inventorySource?: 'CIS' | 'OMS',
): CertonaInventoryResponse | undefined => {
  return getFromCache(queryClient, { skuIds, country, storeId, inventorySource });
};

export const getCertonaInventoryQueryKey = (
  skuIds: string[],
  country: Country,
  storeId: string,
  inventorySource?: 'CIS' | 'OMS',
) => {
  return certonaInventoryQuery.getFullKey({ skuIds, country, storeId, inventorySource });
};
